import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import IntegrationApi from "../../apis/integrationApi";
import { NEUTRAL, ORANGE, VIOLET } from "../../styles/brandTokens";
import type {
  TheoOverview as TheoOverviewData,
  TheoOverviewFilterOptions,
} from "../../types/integration";
import { TopList, cardSx, text } from "../monitor/SummaryPanel";
import OverviewEmptyState from "./OverviewEmptyState";
import OverviewFilterBar from "./OverviewFilterBar";
import OverviewHero from "./OverviewHero";
import OverviewSourceDialog, { type OverviewSourceKind } from "./OverviewSourceDialog";
import TechniciansDialog from "./TechniciansDialog";
import TimeSavedDialog from "./TimeSavedDialog";
import {
  OverviewFilters,
  SOURCE_COLORS,
  assistedTechnicians,
  emptyStateKind,
  readOverviewFilters,
  scopeLabel,
  sourceScopeLabel,
  ticketTypeRows,
  timeSavedByActionRows,
  workRows,
  writeOverviewFilters,
} from "./theoOverviewModel";

const integrationApi = new IntegrationApi();
const NO_OPTIONS: TheoOverviewFilterOptions = { agents: [], customers: [] };

const SourceKey: React.FC = () => (
  <Box component="span" sx={{ display: "inline-flex", gap: 1.5 }}>
    {(
      [
        ["agents", "Agents"],
        ["ticket_chat", "Technicians"],
      ] as const
    ).map(([source, label]) => (
      <Box
        key={source}
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          ...text(12, NEUTRAL.textMuted),
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 8,
            height: 4,
            borderRadius: 2,
            background: SOURCE_COLORS[source],
          }}
        />
        {label}
      </Box>
    ))}
  </Box>
);

function useOverviewData(filters: OverviewFilters, refreshToken: number) {
  const [overview, setOverview] = useState<TheoOverviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Filters can change faster than the server answers; only the latest request may land.
  const requestRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestRef.current;
    const isLatest = () => requestId === requestRef.current;
    setLoading(true);
    setError(null);
    integrationApi
      .getTheoOverview({
        window: filters.window,
        agentId: filters.agent ? Number(filters.agent) : undefined,
        customerId: filters.customer || undefined,
      })
      .then((data) => {
        if (isLatest()) setOverview(data);
      })
      .catch((err: unknown) => {
        if (isLatest())
          setError(
            err instanceof Error ? err.message : "Couldn't load the overview.",
          );
      })
      .finally(() => {
        if (isLatest()) setLoading(false);
      });
  }, [filters.window, filters.agent, filters.customer, refreshToken]);

  return { overview, loading, error };
}

function useFilterOptions(refreshToken: number) {
  const [options, setOptions] = useState<TheoOverviewFilterOptions | null>(
    null,
  );
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    integrationApi
      .getTheoOverviewFilterOptions()
      .then((data) => {
        if (!active) return;
        setOptions(data);
        setFailed(false);
      })
      .catch(() => {
        if (!active) return;
        // The page still works unfiltered; keep the last good list.
        setOptions((current) => current || NO_OPTIONS);
        setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [refreshToken]);
  return { options, failed };
}

const TheoOverview: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => readOverviewFilters(searchParams),
    [searchParams],
  );
  const [refreshToken, setRefreshToken] = useState(0);
  const [explainOpen, setExplainOpen] = useState(false);
  const [techniciansOpen, setTechniciansOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState<OverviewSourceKind | null>(null);
  const { overview, loading, error } = useOverviewData(filters, refreshToken);
  const { options, failed: optionsFailed } = useFilterOptions(refreshToken);

  const updateFilters = useCallback(
    (patch: Partial<OverviewFilters>) =>
      setSearchParams((current) => writeOverviewFilters(current, patch)),
    [setSearchParams],
  );

  const agentName = filters.agent
    ? options?.agents.find((agent) => agent.value === filters.agent)?.label ||
      overview?.agent_tickets.by_agent.find(
        (agent) => agent.agent_id === Number(filters.agent),
      )?.name ||
      filters.agent
    : null;
  const allAgents = !filters.agent;
  const emptyKind =
    overview && !error ? emptyStateKind(overview, filters) : null;
  return (
    <Box sx={{ width: "100%", minHeight: "100%", background: NEUTRAL.page }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: { xs: 26, md: 32 },
                fontWeight: 800,
                color: "#000",
              }}
            >
              Theo Overview
            </Typography>
            <Typography sx={{ ...text(13.5, NEUTRAL.textSecondary), mt: 0.25 }}>
              Everything Theo did, on its own and with your technicians.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => setRefreshToken((value) => value + 1)}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              background: VIOLET.main,
              flexShrink: 0,
              "&:hover": { background: VIOLET.dark },
            }}
          >
            Refresh
          </Button>
        </Stack>

        {/* A brand-new account has nothing to filter; the filters come back once there is work. */}
        {emptyKind === "new" ? null : (
          <Box sx={{ ...cardSx, px: { xs: 2, md: 2.5 }, py: 1.5, mb: 2 }}>
            <OverviewFilterBar
              filters={filters}
              options={options}
              optionsFailed={optionsFailed}
              customerName={overview?.filters.customer_name || null}
              loading={loading && Boolean(overview)}
              refreshedAt={overview?.refreshed_at || null}
              onChange={updateFilters}
            />
          </Box>
        )}

        {error ? (
          <Box sx={{ ...cardSx, p: 2.5 }}>
            <Typography role="alert" sx={text(13.5, ORANGE.text)}>
              {error}
            </Typography>
          </Box>
        ) : !overview ? (
          <Box sx={{ ...cardSx, p: 2.5 }}>
            <CircularProgress
              size={22}
              sx={{ color: VIOLET.main }}
              aria-label="Loading overview"
            />
          </Box>
        ) : emptyKind ? (
          <OverviewEmptyState
            kind={emptyKind}
            canSetUpAgents
            onShowAll={() =>
              updateFilters({ window: "all", agent: "", customer: "" })
            }
          />
        ) : (
          <Box
            aria-busy={loading}
            sx={{
              display: "grid",
              gap: 2,
              opacity: loading ? 0.6 : 1,
              transition: "opacity 150ms ease",
            }}
          >
            <OverviewHero
              overview={overview}
              agentName={agentName}
              onExplainTimeSaved={() => setExplainOpen(true)}
              onShowTechnicians={() => setTechniciansOpen(true)}
              onShowSource={setSourceOpen}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: allAgents
                    ? "repeat(3, minmax(0, 1fr))"
                    : "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              <TopList
                title="Top ticket types"
                rows={ticketTypeRows(overview, allAgents)}
                emptyText="No tickets in this window."
                legend={
                  allAgents && overview.top_ticket_types.length > 0 ? (
                    <SourceKey />
                  ) : undefined
                }
              />
              <TopList
                title="Time saved by action"
                rows={timeSavedByActionRows(overview)}
                emptyText={
                  overview.actions
                    ? "Theo hasn't taken any actions in this window."
                    : "Couldn't load Theo's actions."
                }
                legend={
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setExplainOpen(true)}
                    sx={{
                      border: 0,
                      background: "transparent",
                      p: 0,
                      cursor: "pointer",
                      ...text(12, VIOLET.main, 600),
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    How it's estimated
                  </Box>
                }
              />
              {allAgents ? (
                <TopList
                  title="Work by agent and technician"
                  rows={workRows(overview).map((row) => ({
                    ...row,
                    onClick:
                      row.kind === "agent"
                        ? () => updateFilters({ agent: row.id })
                        : () => setTechniciansOpen(true),
                  }))}
                  emptyText="No agent or technician has worked a ticket in this window."
                  legend={<SourceKey />}
                />
              ) : null}
            </Box>
          </Box>
        )}
      </Box>
      <TimeSavedDialog
        open={explainOpen}
        onClose={() => setExplainOpen(false)}
        overview={overview}
      />
      <OverviewSourceDialog
        kind={sourceOpen}
        onClose={() => setSourceOpen(null)}
        query={{
          window: filters.window,
          agentId: filters.agent ? Number(filters.agent) : undefined,
          customerId: filters.customer || undefined,
        }}
        scopeLabel={sourceScopeLabel(filters, overview?.filters.customer_name || null, agentName)}
        onExplainTimeSaved={() => {
          setSourceOpen(null);
          setExplainOpen(true);
        }}
      />
      <TechniciansDialog
        open={techniciansOpen}
        onClose={() => setTechniciansOpen(false)}
        technicians={assistedTechnicians(overview)}
        scopeLabel={scopeLabel(
          filters,
          overview?.filters.customer_name || null,
        )}
      />
    </Box>
  );
};

export default TheoOverview;
